import { Button, Dropdown, Loader } from "@caprikorn/dynamo-ui";
import { createContext, useContext, useMemo, useState } from "react";

import { DevPanel } from "@components/DevPanel/DevPanel";
import { ErrorTemplate } from "@components/ErrorTemplate/ErrorTemplate";
import { IS_TEST_ENV } from "@core/core.constants";
import { WindowTerminal } from "@icons";
import type { TenantConfig } from "@services/tenant/tenant.types";
import { useGetTenantConfig } from "@services/tenant/useGetTenantConfig";

import classes from "./Tenant.module.css";
import type { TenantContextProps, TenantProviderProps } from "./Tenant.types";

const TenantContext = createContext<TenantContextProps | null>(null);

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const { data: tenantConfig, isPending, isError } = useGetTenantConfig();
  const [isDevPanelOpen, setIsDevPanelOpen] = useState(false);

  const value = useMemo(
    (): TenantContextProps | null => ({
      ...tenantConfig,
      isFeatureEnabled: (feature: keyof TenantConfig) =>
        Boolean(tenantConfig?.[feature]),
    }),
    [tenantConfig],
  );

  if (isPending) {
    return (
      <div className={classes.container}>
        <Loader size={50} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={classes.container}>
        <ErrorTemplate />
        {IS_TEST_ENV && (
          <Dropdown
            isOpen={isDevPanelOpen}
            onOpenChange={setIsDevPanelOpen}
            renderTrigger={(props) => (
              <Button
                {...props}
                className={classes.devPanel}
                startIcon={WindowTerminal}
                variant="secondary"
              >
                Dev Panel
              </Button>
            )}
          >
            <DevPanel />
          </Dropdown>
        )}
      </div>
    );
  }

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextProps => {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }

  return context;
};
